import { useMemo } from "react";
import { Receipt } from "lucide-react";
import { convertCurrency, formatCurrency } from "@/lib/currencyConversion";
import { useLanguage } from "@/contexts/LanguageContext";
import { motion } from "framer-motion";
import { DashboardCard } from "@/components/ui/dashboard-card";

interface Expense {
  id: string;
  amount: number;
  merchant: string;
  category: string;
  expense_date: string;
  currency: string;
}

interface MonthSummaryProps {
  expenses: Expense[];
  selectedMonth: Date;
  userCurrency: string;
  previousMonthTotal?: number;
}

export const MonthSummary = ({
  expenses,
  selectedMonth,
  userCurrency,
  previousMonthTotal,
}: MonthSummaryProps) => {
  const { t } = useLanguage();

  const summary = useMemo(() => {
    const total = expenses.reduce((sum, e) => {
      return sum + convertCurrency(e.amount, e.currency, userCurrency);
    }, 0);

    const count = expenses.length;

    // Calculate change from previous month
    let changePercent: number | null = null;
    if (previousMonthTotal !== undefined && previousMonthTotal > 0) {
      changePercent = ((total - previousMonthTotal) / previousMonthTotal) * 100;
    }

    return { total, count, changePercent };
  }, [expenses, previousMonthTotal, userCurrency]);

  const formatMonth = (date: Date) => {
    return date.toLocaleDateString('en-US', { 
      month: 'long',
      year: 'numeric'
    });
  };

  // Only show trend badge if change is significant (>5%)
  const showTrendBadge = summary.changePercent !== null && Math.abs(summary.changePercent) > 5;
  const trendIsDown = summary.changePercent !== null && summary.changePercent < 0;
  const absChange = summary.changePercent !== null ? Math.abs(summary.changePercent).toFixed(0) : "0";

  if (expenses.length === 0) {
    return (
      <motion.div 
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <DashboardCard className="p-6 text-center">
          <Receipt className="w-12 h-12 mx-auto text-muted-foreground/50 mb-3" />
          <p className="text-muted-foreground">No expenses in {formatMonth(selectedMonth)}</p>
        </DashboardCard>
      </motion.div>
    );
  }

  return (
    <motion.div 
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <DashboardCard className="p-5">
        {/* Header row: month + compact trend badge */}
        <div className="flex items-center justify-between mb-2">
          <p className="text-label-card">{formatMonth(selectedMonth)}</p>
          {showTrendBadge && (
            <span 
              className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                trendIsDown 
                  ? "bg-success/15 text-success" 
                  : "bg-destructive/15 text-destructive"
              }`}
            >
              {trendIsDown ? "↓" : "↑"} {absChange}%
            </span>
          )}
        </div>
        
        {/* THE ONE NUMBER - centered and prominent */}
        <p className="text-amount-large text-center mb-2">
          {formatCurrency(summary.total, userCurrency)}
        </p>
        
        {/* Single meta line */}
        <p className="text-meta text-center">
          {summary.count} expense{summary.count !== 1 ? 's' : ''} this month
        </p>
      </DashboardCard>
    </motion.div>
  );
};
